// pages/PatientAppointments.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { ArrowLeft } from "lucide-react"; // 👈 back icon
import { db, auth } from "../firebase";
import AppointmentsSection from "../components/patient/AppointmentsSection";
import ReviewModal from "../components/patient/ReviewModal"; // 🔹 adjust path if needed
import MedicalRecordModal from "../components/patient/MedicalRecordModal"; // 🔹 adjust path if needed

/** Normalize appointment shape for AppointmentsSection */
function normalizeAppointment(a) {
  return {
    id: a.id,
    status: a.status || "pending",

    serviceName: a.serviceName || "Konsultë",
    servicePrice: a.servicePrice || null,

    scheduledDate: a.scheduledDate || null,
    scheduledTime: a.scheduledTime || null,

    appointmentType:
      a.appointmentType === "in-person"
        ? "in-clinic"
        : a.appointmentType || "in-clinic",

    doctorInfo: {
      name: a.doctorInfo?.name || a.name || "Mjek pa emër",
      specialty:
        a.doctorInfo?.specialty ||
        a.specialty ||
        "Mjekësi e përgjithshme",
      photoUrl:
        a.doctorInfo?.photoUrl ||
        a.photoUrl ||
        a.doctorPhotoUrl ||
        null,
      uid: a.doctorInfo?.uid || a.doctorId,
    },

    patientName: a.patientInfo?.name || "",
    patientPhone: a.patientInfo?.phone || "",
    patientEmail: a.patientInfo?.email || "",

    reason: a.reason || "",
    notes: a.notes || "",

    cancelReason: a.cancelReason || "",

    createdAt: a.createdAt || new Date(),
    doctorId: a.doctorId,
  };
}

export default function PatientAppointments() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [appointments, setAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);

  // 🔹 for review modal
  const [reviewAppointment, setReviewAppointment] = useState(null);

  // 🔹 for medical record modal
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Load appointments
  useEffect(() => {
    if (!user) return;

    const qAppt = query(
      collection(db, "appointments"),
      where("patientId", "==", user.uid)
    );

    const unsub = onSnapshot(qAppt, (snap) => {
      const rows = snap.docs.map((d) =>
        normalizeAppointment({ id: d.id, ...d.data() })
      );
      setAppointments(rows);
    });

    return () => unsub();
  }, [user]);

  // Load medical records
  useEffect(() => {
    if (!user) return;

    const qRec = query(
      collection(db, "medicalRecords"),
      where("patientId", "==", user.uid)
    );

    const unsub = onSnapshot(qRec, (snap) => {
      setMedicalRecords(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    });

    return () => unsub();
  }, [user]);

  return (
    <div style={{ padding: 16 }}>
      {/* Top bar with back button */}
      <div
        style={{
          maxWidth: 420,
          margin: "0 auto 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Kthehu mbrapa"
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "1px solid #E5E7EB",
            background: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <ArrowLeft style={{ width: 18, height: 18, color: "#4B5563" }} />
        </button>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#0F172A",
          }}
        >
          Takimet e mia
        </div>
      </div>

      <AppointmentsSection
        appointments={appointments}
        medicalRecords={medicalRecords}
        // ✅ open medical record modal (no navigate)
        onRecordSelect={(record) => setSelectedRecord(record)}
        // ✅ open review modal (already modal-based)
        onReviewSelect={(appt) => setReviewAppointment(appt)}
      />

      {/* ⭐ Review modal */}
      {reviewAppointment && (
        <ReviewModal
          appointment={reviewAppointment}
          onClose={() => setReviewAppointment(null)}
          onSubmitted={() => {
            // optional: close after submit
            setReviewAppointment(null);
          }}
        />
      )}

      {/* 📄 Medical record modal */}
      {selectedRecord && (
        <MedicalRecordModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
}
